import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { NotificationToast, SessionResultOverlay } from '../GameEffects';
import { ColyseusBaseGameRoomTransport } from '../../realtime/colyseusBaseGameRoomTransport';
import {
  duplicateSessionUserMessage,
  finalDisconnectUserMessage,
  isReconnectFallbackJoin,
  reconnectSuccessUserMessage,
} from '../../realtime/connectionUx';
import { SEKA_GAME_COMMAND } from '../../contracts/sekaV1';
import {
  belotCardIdToPlaying,
  belotMatchCpFromEnginePoints,
  belotMatchTargetCpFromEngine,
  formatAuctionBid,
  formatBelotCard,
} from '../../game/belotArmenianCards';
import { legalPlays } from '../../shared/game/belot/armenian/tricks';
import { mapSnapshotSheetToHeroRows } from '../../game/belotArmenian/scoreSheet';
import {
  ArmenianBelotScoreSheetPanel,
  ArmenianBelotScoreSheetTrigger,
} from '../belot/ArmenianBelotScoreSheetPanel';
import { ArmenianAuctionPanel } from '../belot/ArmenianAuctionPanel';
import { PlayingCard } from '../../poker-ui/PlayingCard';
import { AudioProvider, useAudio } from '../../poker-ui/AudioProvider';
import { ArcadeModeBackdrop } from '../../poker-ui/ArcadeModeBackdrop';
import { GameIndustrialHeader } from '../../poker-ui/GameIndustrialHeader';
import { OnlineBelotTable } from './OnlineBelotTable';
import { BelotHardcoreDeclareCombosPanel } from '../belot/BelotHardcoreDeclareCombosPanel';
import { useBelotComboAnnounceCardReveal } from '../belot/hooks/useBelotComboAnnounceCardReveal';
import { OnlineOpenBelotTable } from './OnlineOpenBelotTable';
import {
  buildOpenDuellOnlineDebugReport,
  OpenDuellOnlineEngineDebugPanel,
} from './OpenDuellOnlineEngineDebugPanel';
import { BelotOnlineRevealOverlay } from './BelotOnlineRevealOverlay';
import {
  useBelotOnlineFxBus,
  ONLINE_BELOT_AUCTION_REVEAL_MS,
} from './hooks/useBelotOnlineFxBus';
import {
  useBelotOnlineHeroTurnTimer,
  ONLINE_BELOT_HERO_TURN_SECONDS,
} from './hooks/useBelotOnlineHeroTurnTimer';
import { useBelotSeatLayout } from '../belot/hooks/useBelotSeatLayout';
import { cn } from '../../poker-ui/utils/cn';
import { Sparkles, Trophy, Skull } from 'lucide-react';

const { computeOpenDuellLegalPlayCardIds } = require('../../shared/game/belot/openDuell/openDuellClientLegal');
const { detectCombinations } = require('../../shared/game/belot/armenian/declarations');

const BELOT_PHASE_RU = {
  init: 'ожидание',
  dealing: 'перед раздачей',
  auction: 'торги',
  declare: 'объявления',
  play: 'взятки',
  score: 'подсчёт',
  match_end: 'матч окончен',
};

function belotTeamOfSeat(seat) {
  return seat === 0 || seat === 2 ? 0 : 1;
}

/**
 * Минимальный UI для Colyseus PokerRoom / BelotRoom (BaseGameRoom): снимок `game/state`, базовые команды.
 *
 * @param {{
 *   tableId: string,
 *   gameRoomId?: string,
 *   colyseusRoomName: 'poker' | 'belot',
 *   title?: string,
 *   user: { id?: string, username?: string },
 *   accessToken?: string | null,
 *   onBack: () => void,
 *   returnView?: string,
 *   onMatchAborted?: (detail: { payload?: object | null, returnView?: string }) => void,
 * }} props
 */
function BaseGameRoomScreenInner({
  tableId,
  gameRoomId = '',
  colyseusRoomName,
  title,
  user,
  accessToken,
  onBack,
  incomingInvite = null,
  onRespondInvite,
  onSessionOutcome,
  returnView = 'online-lobby',
  onMatchAborted,
}) {
  const { play, audioUnlocked } = useAudio();
  const [snapshot, setSnapshot] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const transportRef = useRef(null);
  const reconnectToastAtRef = useRef(0);
  const matchAbortHandledRef = useRef(false);
  const [showInvitePicker, setShowInvitePicker] = useState(false);
  const [inviteVisible, setInviteVisible] = useState(false);
  const inviteHideTimerRef = useRef(null);
  const [belotPrivateHole, setBelotPrivateHole] = useState(null);
  const [belotScoreSheetOpen, setBelotScoreSheetOpen] = useState(false);
  const [belotPeekLastTrick, setBelotPeekLastTrick] = useState(false);
  const [belotAuctionRemountKey, setBelotAuctionRemountKey] = useState(0);
  /** После реконнекта: один цикл подавления «полёта» карт / лишних motion-эффектов до смены раздачи. */
  const [belotHydrateSuppressAnim, setBelotHydrateSuppressAnim] = useState(false);
  /** Реконнект / F5: не озвучивать «догоняющий» стейт; до первого жеста — политика autoplay. */
  const belotAudioSuppressed = belotHydrateSuppressAnim || !audioUnlocked;
  /** Раздача (4p или open 1×1): блок ввода как в локальном лобби. */
  const [belotDealAnimActive, setBelotDealAnimActive] = useState(false);
  /** GSAP-полёт карты до отправки PLAY_CARD (open 1×1 и 4p армянский/молдавский). */
  const [belotPlayFlightActive, setBelotPlayFlightActive] = useState(false);
  const [sessionOutcomeOverlay, setSessionOutcomeOverlay] = useState(null);
  const [matchAbortOverlayOutcome, setMatchAbortOverlayOutcome] = useState(null);
  const matchAbortPendingPayloadRef = useRef(null);
  const matchAbortPostOverlayRef = useRef(null);
  const matchAbortOverlayActiveRef = useRef(false);
  const handledOutcomeIdsRef = useRef(new Set());
  const [belotRevealFx, setBelotRevealFx] = useState(null);
  const belotRevealClearTimerRef = useRef(null);
  const contractBadgeAnchorRef = useRef(null);
  const belotOpenSeatArenaRef = useRef(null);
  const deckRef = useRef(null);
  const trickCenterRef = useRef(null);
  const heroSeatAnchorRef = useRef(null);
  const [belotPendingCommands, setBelotPendingCommands] = useState({});
  const belotPendingByTypeRef = useRef({});
  const belotLastSyncSeqRef = useRef(0);
  /** Для лога команд в отладке open 1×1: актуализируется после вычисления `isOpenDuellBelot`. */
  const isOpenDuellBelotRef = useRef(false);
  const belotOnlineDiagLoggedRef = useRef('');
  const [openDuellEngineDebugOpen, setOpenDuellEngineDebugOpen] = useState(false);
  const [openDuellClientCmdLog, setOpenDuellClientCmdLog] = useState([]);
  const [openDuellSnapTimeline, setOpenDuellSnapTimeline] = useState([]);
  const matchEndAnimRafRef = useRef(null);
  const [matchEndDisplayScores, setMatchEndDisplayScores] = useState(null);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const appendOpenDuellClientLog = useCallback((entry) => {
    setOpenDuellClientCmdLog((prev) => [...prev.slice(-299), entry]);
  }, []);

  const displayTitle =
    title || (colyseusRoomName === 'belot' ? 'Белот (онлайн)' : 'Покер (онлайн)');

  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      setError('Нужна авторизация');
      return undefined;
    }
    let cancelled = false;
    let transport = null;
    /** @type {null | (() => void)} */
    let removeTransportUiListeners = null;
    matchAbortHandledRef.current = false;

    const pushToast = (message, type = 'info') => {
      if (cancelled) return;
      setToasts((prev) => [
        ...prev,
        { id: `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`, message, type },
      ]);
    };

    (async () => {
      try {
        transport = await ColyseusBaseGameRoomTransport.join({
          tableId,
          user,
          gameRoomId,
          roomName: colyseusRoomName,
          accessToken: accessToken || undefined,
        });
        if (cancelled) {
          transport.disconnect();
          return;
        }
        transportRef.current = transport;
        setError('');
        transport.onGameState((payload) => {
          if (!cancelled) setSnapshot(payload || null);
          setLoading(false);
        });
        if (colyseusRoomName === 'belot') {
          transport.onPrivateHole((payload) => {
            if (!cancelled) setBelotPrivateHole(payload && typeof payload === 'object' ? payload : null);
          });
        }
        transport.onError((payload) => {
          if (cancelled) return;
          const commandId = String(payload?.details?.commandId || '');
          if (commandId) {
            setBelotPendingCommands((prev) => {
              if (!prev[commandId]) return prev;
              const next = { ...prev };
              const cmdType = next[commandId]?.commandType;
              if (cmdType && belotPendingByTypeRef.current[cmdType] === commandId) {
                delete belotPendingByTypeRef.current[cmdType];
              }
              delete next[commandId];
              return next;
            });
          }
          const rawMsg = String(payload?.message || payload?.error || 'Ошибка сервера');
          const friendly = /illegal card/i.test(rawMsg)
            ? 'Ход не принят: состояние устарело или ход уже обработан.'
            : rawMsg;
          setError(friendly);
        });
        transport.onAck((payload) => {
          if (cancelled) return;
          const commandId = String(payload?.commandId || '');
          if (!commandId) return;
          setBelotPendingCommands((prev) => {
            if (!prev[commandId]) return prev;
            const cmdType = String(prev[commandId]?.commandType || '');
            /* PLAY_CARD: не снимать pending до роста belotSync.seq — иначе второй клик уходит до нового GAME_STATE и даёт illegal card. */
            if (cmdType === 'PLAY_CARD') return prev;
            const next = { ...prev };
            if (cmdType && belotPendingByTypeRef.current[cmdType] === commandId) {
              delete belotPendingByTypeRef.current[cmdType];
            }
            delete next[commandId];
            return next;
          });
        });

        /** @type {(payload: unknown) => void} */
        let onDisconnect = () => {};

        const onReconnected = (payload) => {
          if (cancelled) return;
          setBelotPendingCommands({});
          belotPendingByTypeRef.current = {};
          setBelotHydrateSuppressAnim(true);
          window.setTimeout(() => {
            if (!cancelled) setBelotHydrateSuppressAnim(false);
          }, 1000);
          if (!cancelled) setBelotAuctionRemountKey((k) => k + 1);
          const now = Date.now();
          if (now - reconnectToastAtRef.current < 2800) return;
          reconnectToastAtRef.current = now;
          pushToast(
            reconnectSuccessUserMessage({ context: 'room', fallbackJoin: isReconnectFallbackJoin(payload) }),
            'success'
          );
        };
        function detachTransportUi() {
          transport.off('reconnected', onReconnected);
          transport.off('disconnect', onDisconnect);
          transport.off('match_aborted', onMatchAbortedMsg);
        }

        function finishMatchAbort(payload) {
          if (cancelled || matchAbortHandledRef.current) return;
          const raw = payload && typeof payload === 'object' ? payload : {};
          const myId = String(user?.id || '').trim();
          const byUser = raw.matchAbortOutcomesByUserId;
          const mine = myId && byUser && typeof byUser === 'object' ? byUser[myId] : null;

          if (mine) {
            matchAbortHandledRef.current = true;
            matchAbortPendingPayloadRef.current = raw;
            matchAbortOverlayActiveRef.current = true;
            matchAbortPostOverlayRef.current = () => {
              const p = matchAbortPendingPayloadRef.current || raw;
              const afk =
                Boolean(p?.afkMatchAbort) || String(p?.reason || '') === 'player_afk';
              pushToast(
                afk
                  ? 'Партия отменена: бездействие (много пропусков хода).'
                  : 'Партия отменена: игрок вышел из матча.',
                'warning'
              );
              detachTransportUi();
              try {
                transport?.disconnect();
              } catch {
                /* ignore */
              }
              onMatchAborted?.({ payload: p || null, returnView });
            };
            setMatchAbortOverlayOutcome(mine);
            onSessionOutcome?.(mine);
            return;
          }

          matchAbortHandledRef.current = true;
          const afk =
            Boolean(raw?.afkMatchAbort) || String(raw?.reason || '') === 'player_afk';
          pushToast(
            afk
              ? 'Партия отменена: бездействие (много пропусков хода).'
              : 'Партия отменена: игрок вышел из матча.',
            'warning'
          );
          detachTransportUi();
          try {
            transport?.disconnect();
          } catch {
            /* ignore */
          }
          onMatchAborted?.({ payload: raw || null, returnView });
        }

        function onMatchAbortedMsg(payload) {
          finishMatchAbort(payload && typeof payload === 'object' ? payload : {});
        }

        removeTransportUiListeners = detachTransportUi;

        onDisconnect = (payload) => {
          if (cancelled || payload?.intentional) return;
          if (payload?.belotRematchTimeout) {
            pushToast('Время на «Готов к реваншу» истекло — вы сняты с места.', 'warning');
            if (!cancelled) {
              setError('Время на голосование за реванш истекло. Вернитесь в лобби, чтобы открыть стол снова.');
            }
            return;
          }
          if (payload?.matchAborted) {
            finishMatchAbort(payload);
            return;
          }
          if (payload?.duplicateSession) {
            pushToast(duplicateSessionUserMessage('room'), 'warning');
            return;
          }
          pushToast(finalDisconnectUserMessage('room', payload?.error), 'error');
        };

        transport.on('reconnected', onReconnected);
        transport.on('disconnect', onDisconnect);
        transport.on('match_aborted', onMatchAbortedMsg);
      } catch (e) {
        console.error('[BaseGameRoomScreen]', e);
        if (!cancelled) {
          setError('Не удалось подключиться к комнате');
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      transportRef.current = null;
      setBelotPrivateHole(null);
      setBelotPendingCommands({});
      belotPendingByTypeRef.current = {};
      if (transport) {
        removeTransportUiListeners?.();
        transport.disconnect();
      }
    };
  }, [tableId, gameRoomId, colyseusRoomName, accessToken, user, returnView, onMatchAborted, onSessionOutcome]);

  const sendBelotCommand = useCallback(
    (command) => {
      const commandType = String(command?.type || '').trim();
      if (!commandType) return false;
      const inFlightId = belotPendingByTypeRef.current[commandType];
      if (inFlightId) return false;
      const commandId = transportRef.current?.sendGameCommand(SEKA_GAME_COMMAND.BELOT_APPLY, { command }) || '';
      if (!commandId) return false;
      if (isOpenDuellBelotRef.current) {
        appendOpenDuellClientLog({
          at: Date.now(),
          iso: new Date().toISOString(),
          commandType,
          command,
        });
      }
      belotPendingByTypeRef.current[commandType] = commandId;
      setBelotPendingCommands((prev) => ({
        ...prev,
        [commandId]: {
          commandType,
          at: Date.now(),
        },
      }));
      return true;
    },
    [appendOpenDuellClientLog],
  );

  const resolveInvite = useCallback(
    (decision) => {
      const inviteId = String(incomingInvite?.inviteId || '').trim();
      const normalized = String(decision || '').trim().toLowerCase();
      if (!inviteId || !normalized) return;
      transportRef.current?.sendGameCommand('social/resolve-invite', {
        inviteId,
        decision: normalized,
      });
      onRespondInvite?.(inviteId, normalized, { source: 'game' });
      setInviteVisible(false);
      setShowInvitePicker(false);
    },
    [incomingInvite, onRespondInvite]
  );

  useEffect(() => {
    if (!incomingInvite?.inviteId) {
      setInviteVisible(false);
      setShowInvitePicker(false);
      if (inviteHideTimerRef.current) {
        clearTimeout(inviteHideTimerRef.current);
        inviteHideTimerRef.current = null;
      }
      return;
    }
    setInviteVisible(true);
    setShowInvitePicker(false);
    if (inviteHideTimerRef.current) clearTimeout(inviteHideTimerRef.current);
    inviteHideTimerRef.current = setTimeout(() => {
      setInviteVisible(false);
    }, 10000);
    return () => {
      if (inviteHideTimerRef.current) {
        clearTimeout(inviteHideTimerRef.current);
        inviteHideTimerRef.current = null;
      }
    };
  }, [incomingInvite?.inviteId]);

  let publicStateParsed = null;
  try {
    const raw = snapshot?.publicState;
    if (typeof raw === 'string' && raw) {
      publicStateParsed = JSON.parse(raw);
    } else if (raw && typeof raw === 'object') {
      publicStateParsed = raw;
    }
  } catch {
    publicStateParsed = null;
  }

  const players = useMemo(
    () => (Array.isArray(snapshot?.players) ? snapshot.players : []),
    [snapshot?.players]
  );

  const belotArmenian = snapshot?.belotArmenian ?? null;
  const belotOpenDuell = snapshot?.belotOpenDuell ?? null;
  const belotSync = snapshot?.belotSync && typeof snapshot.belotSync === 'object' ? snapshot.belotSync : null;
  const sessionOutcomeEvents = useMemo(
    () => (Array.isArray(snapshot?.sessionOutcomeEvents) ? snapshot.sessionOutcomeEvents : []),
    [snapshot?.sessionOutcomeEvents]
  );
  const belotSeats = useMemo(() => {
    const belotSeatsRaw = snapshot?.belotSeats;
    return belotSeatsRaw && typeof belotSeatsRaw === 'object' && !Array.isArray(belotSeatsRaw) ? belotSeatsRaw : {};
  }, [snapshot?.belotSeats]);
  const belotRematchReadyUserIds = useMemo(() => {
    const raw = snapshot?.belotRematchReadyUserIds;
    if (!Array.isArray(raw)) return [];
    return raw.map((id) => String(id || '').trim()).filter(Boolean);
  }, [snapshot?.belotRematchReadyUserIds]);
  const belotRematchReadyIdSet = useMemo(() => new Set(belotRematchReadyUserIds), [belotRematchReadyUserIds]);
  const belotRematchRequiredCount = Math.max(0, Number(snapshot?.belotRematchRequiredCount) || 0);
  const belotRematchDeadlineAtMs = useMemo(() => {
    const n = Number(snapshot?.belotRematchDeadlineAtMs);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [snapshot?.belotRematchDeadlineAtMs]);
  const belotRematchBudgetMs = Math.max(1000, Number(snapshot?.belotRematchBudgetMs) || 30_000);
  const belotVariant = String(publicStateParsed?.belotVariant || '').toLowerCase();
  const isOpenDuellBelot = belotVariant === 'open_duell';
  isOpenDuellBelotRef.current = isOpenDuellBelot;
  const isBelotVariantSupported =
    belotVariant === 'armenian' || belotVariant === 'moldovan' || isOpenDuellBelot;
  const belotEnginePublic = isOpenDuellBelot ? belotOpenDuell : belotArmenian;
  const belotTargetScore = publicStateParsed?.belotTargetScore;
  const belotStake = Number(publicStateParsed?.belotStake) || 0;
  const belotLobbyMaxPlayers = useMemo(() => {
    if (String(publicStateParsed?.belotVariant || '').toLowerCase() === 'open_duell') return 2;
    const fromTop = Number(publicStateParsed?.lobbyMaxPlayers);
    const fromOpts = Number(publicStateParsed?.options?.maxPlayers);
    const n = Number.isFinite(fromTop) && fromTop > 0 ? fromTop : fromOpts;
    return n === 2 ? 2 : 4;
  }, [publicStateParsed?.belotVariant, publicStateParsed?.lobbyMaxPlayers, publicStateParsed?.options?.maxPlayers]);
  const myBelotSeat =
    belotPrivateHole && typeof belotPrivateHole.seat === 'number' ? belotPrivateHole.seat : null;
  const myBelotHand = useMemo(
    () => (Array.isArray(belotPrivateHole?.hand) ? belotPrivateHole.hand : []),
    [belotPrivateHole?.hand]
  );
  const myBelotTableStacks = useMemo(
    () => (Array.isArray(belotPrivateHole?.tableStacks) ? belotPrivateHole.tableStacks : []),
    [belotPrivateHole?.tableStacks]
  );
  const belotPhase =
    belotEnginePublic && typeof belotEnginePublic.phase === 'string' ? belotEnginePublic.phase : '';
  const belotTurnSeat =
    belotEnginePublic && typeof belotEnginePublic.currentTurnSeat === 'number'
      ? belotEnginePublic.currentTurnSeat
      : null;
  const belotSeatedHumans = useMemo(() => {
    if (!belotSeats || typeof belotSeats !== 'object') return 0;
    return Object.keys(belotSeats).filter((uid) => String(uid || '').trim()).length;
  }, [belotSeats]);
  const belotSeatRows = useMemo(() => {
    if (!belotSeats || typeof belotSeats !== 'object') return [];
    return [0, 1, 2, 3].map((seat) => {
      const uid = Object.keys(belotSeats).find((k) => belotSeats[k] === seat) || '';
      const pl = players.find((p) => String(p.userId) === String(uid));
      return {
        seat,
        uid,
        name: pl?.username || (uid ? `id …${String(uid).slice(-4)}` : '—'),
        avatarUrl: String(pl?.avatarUrl || '').trim(),
        level: Math.max(1, Math.floor(Number(pl?.level) || 1)),
      };
    });
  }, [belotSeats, players]);
  const canBelotStartRound =
    colyseusRoomName === 'belot' &&
    isBelotVariantSupported &&
    myBelotSeat !== null &&
    (belotPhase === 'init' || belotPhase === 'dealing') &&
    (!isOpenDuellBelot || belotSeatedHumans >= 2);
  const heroBelotRematchReady = Boolean(user?.id && belotRematchReadyIdSet.has(String(user.id)));
  const belotRematchVoteTotal =
    belotRematchRequiredCount > 0 ? belotRematchRequiredCount : Math.max(belotSeatedHumans, belotLobbyMaxPlayers || 2);
  const belotRematchReadyCount = belotRematchReadyUserIds.length;
  const [belotRematchSecLeft, setBelotRematchSecLeft] = useState(null);
  useEffect(() => {
    if (belotPhase !== 'match_end' || belotRematchDeadlineAtMs == null) {
      setBelotRematchSecLeft(null);
      return undefined;
    }
    const tick = () => {
      const ms = belotRematchDeadlineAtMs - Date.now();
      setBelotRematchSecLeft(ms <= 0 ? 0 : Math.ceil(ms / 1000));
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [belotPhase, belotRematchDeadlineAtMs]);
  const myBelotTurnAuction =
    belotPhase === 'auction' && belotTurnSeat !== null && myBelotSeat !== null && belotTurnSeat === myBelotSeat;
  const myBelotTurnPlay =
    belotPhase === 'play' &&
    belotTurnSeat !== null &&
    myBelotSeat !== null &&
    (isOpenDuellBelot
      ? belotTurnSeat === myBelotSeat || belotTurnSeat === myBelotSeat + 2
      : belotTurnSeat === myBelotSeat);
  const belotPendingCount = Object.keys(belotPendingCommands).length;
  const belotInputBlocked = belotPendingCount > 0;
  /** В торгах не гасим UI из‑за анимации раздачи; в розыгрыше раздача/полёт по-прежнему блокируют ввод. */
  const belotOnlineInteractionBlocked = useMemo(
    () =>
      belotInputBlocked ||
      Boolean(belotRevealFx) ||
      (belotPhase !== 'auction' && belotDealAnimActive) ||
      belotPlayFlightActive,
    [belotInputBlocked, belotRevealFx, belotPhase, belotDealAnimActive, belotPlayFlightActive],
  );

  const belotAuctionPanelVariant = isOpenDuellBelot || belotVariant === 'moldovan' ? 'moldovan' : 'armenian';
  const belotAuctionPassLabel = belotAuctionPanelVariant === 'moldovan' ? 'PAS' : 'Пас';
  const belotAuctionOrderLabel = belotAuctionPanelVariant === 'moldovan' ? 'STRIGARE' : 'Заказать';
  const belotAuctionTurnHeadline =
    belotAuctionPanelVariant === 'moldovan' ? 'Ваш ход в bazar' : 'Ваш ход в торгах';

  const belotAuction =
    belotEnginePublic && belotEnginePublic.auction && typeof belotEnginePublic.auction === 'object'
      ? belotEnginePublic.auction
      : null;
  const myBelotTeam = myBelotSeat != null ? belotTeamOfSeat(myBelotSeat) : null;

  const belotContract =
    belotEnginePublic && typeof belotEnginePublic.contract === 'object' ? belotEnginePublic.contract : null;
  const contractTrumpSuit =
    belotContract && belotContract.trumpSuit !== undefined ? belotContract.trumpSuit : null;

  const armenianShulerLegalCardIds = useMemo(() => {
    if (
      isOpenDuellBelot ||
      belotPhase !== 'play' ||
      !belotArmenian?.shulerEnabled ||
      myBelotSeat == null ||
      !belotContract ||
      belotArmenian.trickLeadSeat == null
    ) {
      return null;
    }
    try {
      const ids = legalPlays(
        myBelotHand,
        Array.isArray(belotArmenian.currentTrick) ? belotArmenian.currentTrick : [],
        contractTrumpSuit,
        belotArmenian.trickLeadSeat,
      );
      return new Set(ids);
    } catch {
      return null;
    }
  }, [
    isOpenDuellBelot,
    belotPhase,
    belotArmenian?.shulerEnabled,
    belotArmenian?.currentTrick,
    belotArmenian?.trickLeadSeat,
    myBelotSeat,
    belotContract,
    contractTrumpSuit,
    myBelotHand,
  ]);

  const myFirstPlayDeclarationsCommitted = useMemo(() => {
    const c = belotArmenian?.firstPlayDeclarationsCommitted;
    if (!Array.isArray(c) || myBelotSeat == null) return true;
    return Boolean(c[myBelotSeat]);
  }, [belotArmenian?.firstPlayDeclarationsCommitted, myBelotSeat]);

  const onlineHardcoreCombos = useMemo(() => {
    if (colyseusRoomName !== 'belot' || isOpenDuellBelot) return [];
    if (belotPhase !== 'play') return [];
    if (String(belotArmenian?.declarationMode || 'casual') !== 'hardcore') return [];
    if (myFirstPlayDeclarationsCommitted) return [];
    if (belotTurnSeat !== myBelotSeat || !myBelotTurnPlay) return [];
    if (!Array.isArray(myBelotHand) || myBelotHand.length !== 8) return [];
    try {
      const list = detectCombinations(myBelotHand);
      if (!list.length) return [];
      return [...list].sort((a, b) => (Number(b.points) || 0) - (Number(a.points) || 0));
    } catch {
      return [];
    }
  }, [
    colyseusRoomName,
    isOpenDuellBelot,
    belotPhase,
    belotArmenian?.declarationMode,
    myFirstPlayDeclarationsCommitted,
    myBelotSeat,
    belotTurnSeat,
    myBelotTurnPlay,
    myBelotHand,
  ]);

  const confirmOnlineHardcoreDeclarations = useCallback(
    (explicitKeys) => {
      const keys =
        explicitKeys != null && Array.isArray(explicitKeys)
          ? explicitKeys.filter((k) => typeof k === 'string' && k.length > 0)
          : [];
      sendBelotCommand({ type: 'COMMIT_FIRST_PLAY_DECLARATIONS', payload: { declaredKeys: keys } });
    },
    [sendBelotCommand],
  );

  const { playReveal: playOnlineComboReveal, revealPortal: onlineComboRevealPortal } =
    useBelotComboAnnounceCardReveal({
      heroSeatAnchorRef,
      seatArenaRef: trickCenterRef,
    });

  /** @type {React.MutableRefObject<null | ((item: { id: string, priority: number, run: () => void | Promise<void>, minGapMs?: number }) => void)>} */
  const hardcoreSelfPreviewEnqueueRef = useRef(null);
  const registerHardcoreSelfPreviewEnqueue = useCallback((fn) => {
    hardcoreSelfPreviewEnqueueRef.current = typeof fn === 'function' ? fn : null;
  }, []);

  const runQueuedOnlineComboCardReveal = useCallback(
    (opts) =>
      new Promise((resolve) => {
        playOnlineComboReveal({
          cardIds: Array.isArray(opts?.cardIds) ? opts.cardIds : [],
          label: typeof opts?.label === 'string' ? opts.label : undefined,
          comboLabelRaw:
            opts && typeof opts === 'object' && typeof opts.comboLabelRaw === 'string'
              ? opts.comboLabelRaw
              : undefined,
          launchAnchorRef: opts?.launchAnchorRef,
          timingPreset: opts?.timingPreset === 'catchup' ? 'catchup' : undefined,
          onComplete: resolve,
        });
      }),
    [playOnlineComboReveal],
  );

  const { openDuellLegalPlayCardIds, openDuellLegalComputeError } = useMemo(() => {
    const idle = /** @returns {{ openDuellLegalPlayCardIds: null, openDuellLegalComputeError: null }} */ () => ({
      openDuellLegalPlayCardIds: null,
      openDuellLegalComputeError: null,
    });
    if (!isOpenDuellBelot || belotPhase !== 'play' || !myBelotTurnPlay || belotOnlineInteractionBlocked)
      return idle();
    if (myBelotSeat !== 0 && myBelotSeat !== 1) return idle();
    if (!belotOpenDuell?.contract) return idle();
    const lead = belotOpenDuell.trickLeadSeat;
    if (lead == null) return idle();
    try {
      const openDuellLegalPlayCardIds = computeOpenDuellLegalPlayCardIds({
        mySeat: /** @type {0|1} */ (myBelotSeat),
        myHand: myBelotHand,
        myTableStacks: myBelotTableStacks,
        currentTrick: Array.isArray(belotOpenDuell.currentTrick) ? belotOpenDuell.currentTrick : [],
        trickLeadSeat: lead,
        contractTrump: contractTrumpSuit,
        currentTurnSeat: belotTurnSeat,
      });
      return { openDuellLegalPlayCardIds, openDuellLegalComputeError: null };
    } catch (e) {
      return {
        openDuellLegalPlayCardIds: null,
        openDuellLegalComputeError: String(e?.message || e),
      };
    }
  }, [
    isOpenDuellBelot,
    belotPhase,
    myBelotTurnPlay,
    belotOnlineInteractionBlocked,
    myBelotSeat,
    belotOpenDuell?.currentTrick,
    belotOpenDuell?.trickLeadSeat,
    belotOpenDuell?.contract,
    contractTrumpSuit,
    belotTurnSeat,
    myBelotHand,
    myBelotTableStacks,
  ]);

  /** Мой ход в розыгрыше, а после расчёта легалов массив пуст — подозрение на рассинхрон привата/публичного снапшота. */
  const openDuellLegalPlayEmptyWhileMyTurn = useMemo(() => {
    if (!isOpenDuellBelot || belotPhase !== 'play' || !myBelotTurnPlay || belotOnlineInteractionBlocked) {
      return false;
    }
    return Array.isArray(openDuellLegalPlayCardIds) && openDuellLegalPlayCardIds.length === 0;
  }, [
    isOpenDuellBelot,
    belotPhase,
    myBelotTurnPlay,
    belotOnlineInteractionBlocked,
    openDuellLegalPlayCardIds,
  ]);

  const openDuellLegalGlitchWarnedRef = useRef('');
  useEffect(() => {
    if (!openDuellLegalPlayEmptyWhileMyTurn) {
      openDuellLegalGlitchWarnedRef.current = '';
      return;
    }
    const trickLen = Array.isArray(belotOpenDuell?.currentTrick) ? belotOpenDuell.currentTrick.length : 0;
    const key = `${Number(belotSync?.seq) || 0}-${belotTurnSeat ?? 'x'}-${belotOpenDuell?.trickIndex ?? 'x'}-${trickLen}`;
    if (openDuellLegalGlitchWarnedRef.current === key) return;
    openDuellLegalGlitchWarnedRef.current = key;
    // eslint-disable-next-line no-console
    console.warn(
      '[OpenDuell] Мой ход в розыгрыше, но список легальных карт пуст — проверьте приватный снапшот (рука/стол) и публичный trickLeadSeat/currentTrick.',
      {
        belotSyncSeq: belotSync?.seq,
        currentTurnSeat: belotTurnSeat,
        trickLeadSeat: belotOpenDuell?.trickLeadSeat,
        trickIndex: belotOpenDuell?.trickIndex,
        currentTrickLen: trickLen,
        mySeat: myBelotSeat,
        handLen: Array.isArray(myBelotHand) ? myBelotHand.length : null,
      },
    );
  }, [
    openDuellLegalPlayEmptyWhileMyTurn,
    belotSync?.seq,
    belotTurnSeat,
    belotOpenDuell?.trickLeadSeat,
    belotOpenDuell?.trickIndex,
    belotOpenDuell?.currentTrick,
    myBelotSeat,
    myBelotHand,
  ]);

  const openDuellLegalComputeErrorWarnedRef = useRef('');
  useEffect(() => {
    if (!openDuellLegalComputeError) {
      openDuellLegalComputeErrorWarnedRef.current = '';
      return;
    }
    const trickLen = Array.isArray(belotOpenDuell?.currentTrick) ? belotOpenDuell.currentTrick.length : 0;
    const key = `${Number(belotSync?.seq) || 0}-${openDuellLegalComputeError}-${trickLen}`;
    if (openDuellLegalComputeErrorWarnedRef.current === key) return;
    openDuellLegalComputeErrorWarnedRef.current = key;
    // eslint-disable-next-line no-console
    console.warn('[OpenDuell] Ошибка при расчёте легальных ходов', {
      message: openDuellLegalComputeError,
      belotSyncSeq: belotSync?.seq,
      currentTurnSeat: belotTurnSeat,
      trickLeadSeat: belotOpenDuell?.trickLeadSeat,
      trickIndex: belotOpenDuell?.trickIndex,
      currentTrickLen: trickLen,
      mySeat: myBelotSeat,
    });
  }, [
    openDuellLegalComputeError,
    belotSync?.seq,
    belotTurnSeat,
    belotOpenDuell?.trickLeadSeat,
    belotOpenDuell?.trickIndex,
    belotOpenDuell?.currentTrick,
    myBelotSeat,
  ]);

  const openDuellBellaNotice = useMemo(() => {
    if (!isOpenDuellBelot || !Array.isArray(belotOpenDuell?.lastEvents)) return null;
    const rev = [...belotOpenDuell.lastEvents].reverse();
    const declEv = rev.find((e) => e?.type === 'OPEN_DUELL_DECLARATIONS_RESOLVED');
    const legacyEv = rev.find((e) => e?.type === 'OPEN_DUELL_BELLA_RESOLVED');
    const ev = declEv || legacyEv;
    if (!ev?.payload) return null;
    const myT = myBelotSeat != null ? belotTeamOfSeat(myBelotSeat) : null;
    const parts = [];

    const bellaPayload = declEv?.payload?.bella ?? declEv?.payload;
    const d = (bellaPayload && bellaPayload.delta) || ev.payload.delta || [0, 0];
    const tw =
      (bellaPayload && bellaPayload.teamsWithBella) || ev.payload.teamsWithBella || [false, false];
    if (tw[0] && d[0]) {
      parts.push(
        myT === 0
          ? `Вам: Белла +${d[0]}`
          : `${belotSeatRows?.[0]?.name || 'Игрок 0'}: +${d[0]} (0+2)`,
      );
    }
    if (tw[1] && d[1]) {
      parts.push(
        myT === 1
          ? `Вам: Белла +${d[1]}`
          : `${belotSeatRows?.[1]?.name || 'Игрок 1'}: +${d[1]} (1+3)`,
      );
    }
    const bellaLine = parts.length ? parts.join(' · ') : null;

    if (declEv?.payload?.combos && myT != null) {
      const oc = declEv.payload.combos.outcome;
      const pts = declEv.payload.declarationPointsByTeam || [0, 0];
      const gr = declEv.payload.combos.grossByTeam || [0, 0];
      let comboRu = '';
      if (gr[0] === 0 && gr[1] === 0) {
        comboRu = 'Комбо: нет';
      } else if (oc === 'tie') {
        comboRu = `Комбо: ничья по силе — зачёт +${pts[0]} / +${pts[1]}`;
      } else if (oc === 'team_a') {
        comboRu =
          myT === 0
            ? `Комбо: вам зачтено +${pts[0]} (соперник сгорел)`
            : `Комбо: соперник +${pts[0]}, у вас 0`;
      } else {
        comboRu =
          myT === 1
            ? `Комбо: вам зачтено +${pts[1]} (соперник сгорел)`
            : `Комбо: соперник +${pts[1]}, у вас 0`;
      }
      const la = declEv.payload.combos.labelsScoringA;
      const lb = declEv.payload.combos.labelsScoringB;
      const detail =
        Array.isArray(la) && la.length
          ? ` (${la.join(', ')})`
          : Array.isArray(lb) && lb.length
            ? ` (${lb.join(', ')})`
            : '';
      const comboLine = comboRu + detail;
      if (bellaLine) return `${bellaLine} · ${comboLine}`;
      return comboLine;
    }

    return bellaLine;
  }, [isOpenDuellBelot, belotOpenDuell?.lastEvents, myBelotSeat, belotSeatRows]);

  const contraMult = Number(belotContract?.contraMultiplier ?? belotAuction?.contraMultiplier ?? 1);
  const auctionMultIsMax = contraMult >= 4;
  const bidAuthorSeatForAuction =
    belotAuction && typeof belotAuction.bidAuthorSeat === 'number' ? belotAuction.bidAuthorSeat : null;
  const auctionContractTeam =
    bidAuthorSeatForAuction != null ? belotTeamOfSeat(bidAuthorSeatForAuction) : null;
  const canBelotContra =
    belotPhase === 'auction' &&
    myBelotTurnAuction &&
    myBelotTeam != null &&
    auctionContractTeam != null &&
    myBelotTeam !== auctionContractTeam &&
    contraMult === 1 &&
    belotAuction?.highestBid != null &&
    String(belotAuction?.phase || '') === 'bidding';
  const canBelotRecontra =
    belotPhase === 'auction' &&
    myBelotTurnAuction &&
    myBelotTeam != null &&
    auctionContractTeam != null &&
    myBelotTeam === auctionContractTeam &&
    contraMult === 2 &&
    belotAuction?.highestBid != null &&
    String(belotAuction?.phase || '') === 'bidding';

  useEffect(() => {
    const seq = Number(belotSync?.seq || 0);
    if (!Number.isFinite(seq)) return;
    if (seq <= belotLastSyncSeqRef.current) return;
    belotLastSyncSeqRef.current = seq;
    setBelotPendingCommands({});
    belotPendingByTypeRef.current = {};
  }, [belotSync?.seq]);

  useEffect(() => {
    const now = Date.now();
    setBelotPendingCommands((prev) => {
      const next = {};
      for (const [id, v] of Object.entries(prev)) {
        if (now - Number(v?.at || 0) < 12_000) next[id] = v;
      }
      if (Object.keys(next).length === Object.keys(prev).length) return prev;
      for (const id of Object.keys(prev)) {
        if (next[id]) continue;
        const cmdType = String(prev[id]?.commandType || '');
        if (cmdType && belotPendingByTypeRef.current[cmdType] === id) {
          delete belotPendingByTypeRef.current[cmdType];
        }
      }
      return next;
    });
  }, [snapshot?.updatedAt]);

  useEffect(() => {
    if (!user?.id) return;
    const mine = sessionOutcomeEvents.filter((x) => String(x?.userId || '') === String(user.id));
    if (!mine.length) return;
    const latest = mine[mine.length - 1];
    const outcomeId = String(latest?.outcomeId || '').trim();
    if (!outcomeId || handledOutcomeIdsRef.current.has(outcomeId)) return;
    handledOutcomeIdsRef.current.add(outcomeId);
    setSessionOutcomeOverlay(latest);
    onSessionOutcome?.(latest);
  }, [sessionOutcomeEvents, user?.id, onSessionOutcome]);

  useEffect(() => {
    handledOutcomeIdsRef.current.clear();
    setSessionOutcomeOverlay(null);
    setMatchAbortOverlayOutcome(null);
    matchAbortPendingPayloadRef.current = null;
    matchAbortPostOverlayRef.current = null;
    matchAbortOverlayActiveRef.current = false;
    matchAbortHandledRef.current = false;
  }, [tableId, gameRoomId]);

  useEffect(() => {
    setOpenDuellClientCmdLog([]);
    setOpenDuellSnapTimeline([]);
    setOpenDuellEngineDebugOpen(false);
  }, [tableId, gameRoomId]);

  useEffect(() => {
    if (!isOpenDuellBelot) return;
    const seq = Number(belotSync?.seq) || 0;
    setOpenDuellSnapTimeline((prev) => {
      if (prev.length && prev[prev.length - 1].belotSyncSeq === seq) return prev;
      const ev = belotOpenDuell?.lastEvents;
      const tcp = belotOpenDuell?.tableColumnsPublic;
      return [
        ...prev.slice(-149),
        {
          at: Date.now(),
          iso: new Date().toISOString(),
          belotSyncSeq: seq,
          snapshotUpdatedAt: snapshot?.updatedAt ?? null,
          handIndex: snapshot?.handIndex ?? null,
          phase: belotPhase || null,
          turnSeat: belotTurnSeat,
          roundIndex: belotOpenDuell?.roundIndex ?? null,
          trickIndex: belotOpenDuell?.trickIndex ?? null,
          teamScores: belotOpenDuell?.teamScores ?? null,
          tricksTakenByTeam: belotOpenDuell?.tricksTakenByTeam ?? null,
          handCounts: belotOpenDuell?.handCounts ?? null,
          tableColumnsPublic: tcp ?? null,
          currentTrick: Array.isArray(belotOpenDuell?.currentTrick)
            ? belotOpenDuell.currentTrick.map((pl) => ({
                seat: pl?.seat,
                card: pl?.card,
                label: formatBelotCard(pl?.card),
              }))
            : [],
          trickLeadSeat: belotOpenDuell?.trickLeadSeat ?? null,
          lastCompletedTrick: belotOpenDuell?.lastCompletedTrick ?? null,
          auction: belotOpenDuell?.auction ?? null,
          contractSnippet: belotOpenDuell?.contract
            ? {
                contractTeam: belotOpenDuell.contract.contractTeam,
                trumpSuit: belotOpenDuell.contract.trumpSuit,
                contractTargetPoints: belotOpenDuell.contract.contractTargetPoints,
                kapot: belotOpenDuell.contract.kapot,
                contraMultiplier: belotOpenDuell.contract.contraMultiplier,
              }
            : null,
          lastEventsTail: Array.isArray(ev) ? ev.slice(-12) : [],
          belotTurnDeadlineAtMs: snapshot?.belotTurnDeadlineAtMs ?? null,
        },
      ];
    });
  }, [
    isOpenDuellBelot,
    belotSync?.seq,
    snapshot?.updatedAt,
    snapshot?.handIndex,
    snapshot?.belotTurnDeadlineAtMs,
    belotPhase,
    belotTurnSeat,
    belotOpenDuell,
  ]);

  useEffect(() => {
    belotOnlineDiagLoggedRef.current = '';
  }, [tableId, gameRoomId]);

  useEffect(() => {
    if (colyseusRoomName !== 'belot' || loading) return;
    if (process.env.NODE_ENV === 'production') return;
    const k = `${tableId}:${String(gameRoomId || '')}`;
    if (belotOnlineDiagLoggedRef.current === k) return;
    belotOnlineDiagLoggedRef.current = k;
    const path = typeof window !== 'undefined' ? window.location.pathname || '' : '';
    // eslint-disable-next-line no-console
    console.info('[belot-online:diag]', {
      tableId,
      gameRoomId: gameRoomId || null,
      path,
      lobbyMaxPlayers: belotLobbyMaxPlayers,
      hasBelotArmenian: Boolean(belotArmenian),
      belotPhase: belotPhase || null,
      privateHoleSeat: myBelotSeat,
    });
    if (!gameRoomId) {
      // eslint-disable-next-line no-console
      console.warn(
        '[belot-online:diag] Пустой gameRoomId: используется joinOrCreate по tableId. Если открыли игру не из онлайн-лобби, проверьте mode и совпадение стола.'
      );
    }
  }, [
    belotArmenian,
    belotLobbyMaxPlayers,
    belotPhase,
    colyseusRoomName,
    gameRoomId,
    loading,
    myBelotSeat,
    tableId,
  ]);

  const belotScoreSheetRows = useMemo(() => {
    const raw =
      belotEnginePublic && Array.isArray(belotEnginePublic.roundScoreSheet)
        ? belotEnginePublic.roundScoreSheet
        : null;
    return mapSnapshotSheetToHeroRows(myBelotTeam, raw);
  }, [myBelotTeam, belotEnginePublic]);

  const belotScoreSheetTarget =
    Number(belotTargetScore ?? belotEnginePublic?.targetScore ?? 10010) || 10010;
  const belotEconomy =
    snapshot?.belotEconomy && typeof snapshot.belotEconomy === 'object' ? snapshot.belotEconomy : null;

  const belotHydrateRoomRef = useRef(undefined);
  const belotHydrateHandRef = useRef(null);
  useEffect(() => {
    const rid = snapshot?.roomId;
    if (belotHydrateRoomRef.current === rid) return;
    belotHydrateRoomRef.current = rid;
    belotHydrateHandRef.current = null;
    setBelotHydrateSuppressAnim(false);
  }, [snapshot?.roomId]);

  useEffect(() => {
    const hi = snapshot?.handIndex;
    if (belotHydrateHandRef.current === hi) return;
    belotHydrateHandRef.current = hi;
    setBelotHydrateSuppressAnim(false);
  }, [snapshot?.handIndex]);

  useEffect(() => {
    if (belotPhase !== 'play') setBelotPeekLastTrick(false);
  }, [belotPhase]);

  useEffect(() => {
    if (belotPhase !== 'auction') setBelotDealAnimActive(false);
  }, [belotPhase]);

  /**
   * Triggers reveal-оверлей при появлении контракта (см. useBelotOnlineFxBus).
   * Длительность синхронизирована с локальным `AUCTION_REVEAL_MS`.
   */
  const handleBelotAuctionEnd = useCallback((reveal) => {
    setBelotRevealFx(reveal);
    if (belotRevealClearTimerRef.current) clearTimeout(belotRevealClearTimerRef.current);
    belotRevealClearTimerRef.current = setTimeout(() => {
      setBelotRevealFx(null);
      belotRevealClearTimerRef.current = null;
    }, ONLINE_BELOT_AUCTION_REVEAL_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (belotRevealClearTimerRef.current) clearTimeout(belotRevealClearTimerRef.current);
    };
  }, []);

  useBelotOnlineFxBus({
    belotArmenian: belotEnginePublic,
    play,
    onAuctionEnd: handleBelotAuctionEnd,
    contractBadgeAnchorRef,
    mySeat: myBelotSeat,
    syncOnlyMode: belotHydrateSuppressAnim,
    audioSuppressed: belotAudioSuppressed,
  });

  const { heroSecLeft } = useBelotOnlineHeroTurnTimer({
    belotArmenian: belotEnginePublic,
    mySeat: myBelotSeat,
    play,
    audioSuppressed: belotAudioSuppressed,
    blockHeroTurnTimer: Boolean(belotRevealFx) || belotDealAnimActive || belotPlayFlightActive,
    turnDeadlineAtMs: snapshot?.belotTurnDeadlineAtMs ?? null,
    openDuellPlaySeats: isOpenDuellBelot,
    turnResyncKey: Number(belotSync?.seq) || 0,
  });

  const openDuellDebugReport = useMemo(() => {
    if (!isOpenDuellBelot) return { humanSummary: '' };
    return buildOpenDuellOnlineDebugReport({
      exportedAt: new Date().toISOString(),
      tableId,
      gameRoomId,
      roomSnapshot: {
        roomId: snapshot?.roomId,
        handIndex: snapshot?.handIndex,
        updatedAt: snapshot?.updatedAt,
        belotEconomy,
        sessionOutcomeEventsTail: Array.isArray(sessionOutcomeEvents)
          ? sessionOutcomeEvents.slice(-8)
          : [],
      },
      publicStateParsed,
      belotVariant,
      belotOpenDuell,
      belotSync,
      belotSeats,
      belotSeatRows,
      myBelotSeat,
      privateHandIds: myBelotHand,
      privateTableStacks: myBelotTableStacks,
      derived: {
        phase: belotPhase,
        belotTurnSeat,
        myBelotTurnAuction,
        myBelotTurnPlay,
        belotOnlineInteractionBlocked,
        belotPendingCommands,
        openDuellBellaNotice,
        belotScoreSheetRows,
        belotScoreSheetTarget,
        belotLobbyMaxPlayers,
        belotSeatedHumans,
        openDuellLegalComputeError,
      },
      belotTurnDeadlineAtMs: snapshot?.belotTurnDeadlineAtMs ?? null,
      heroSecLeft,
      heroTurnSeconds: ONLINE_BELOT_HERO_TURN_SECONDS,
      openDuellLegalPlayCardIds,
      clientCommandsSent: openDuellClientCmdLog,
      belotSyncTimeline: openDuellSnapTimeline,
    });
  }, [
    isOpenDuellBelot,
    tableId,
    gameRoomId,
    snapshot?.roomId,
    snapshot?.handIndex,
    snapshot?.updatedAt,
    snapshot?.belotTurnDeadlineAtMs,
    publicStateParsed,
    belotVariant,
    belotOpenDuell,
    belotSync,
    belotSeats,
    belotSeatRows,
    myBelotSeat,
    myBelotHand,
    myBelotTableStacks,
    belotPhase,
    belotTurnSeat,
    myBelotTurnAuction,
    myBelotTurnPlay,
    belotOnlineInteractionBlocked,
    belotPendingCommands,
    openDuellBellaNotice,
    belotScoreSheetRows,
    belotScoreSheetTarget,
    belotLobbyMaxPlayers,
    belotSeatedHumans,
    belotEconomy,
    sessionOutcomeEvents,
    heroSecLeft,
    openDuellLegalPlayCardIds,
    openDuellLegalComputeError,
    openDuellClientCmdLog,
    openDuellSnapTimeline,
  ]);

  /**
   * Анимированная дельта счёта команд при `match_end` — мимикрия локального
   * `BelotArmenianLocalLobby`-overlay'а (RAF 650-1800 мс, ease-out cubic).
   * Источник правды — `belotArmenian.teamScores`, локальные значения только
   * для рендера счётчика в шапке оверлея.
   */
  useEffect(() => {
    if (belotPhase !== 'match_end' || !Array.isArray(belotEnginePublic?.teamScores)) {
      if (matchEndAnimRafRef.current) {
        cancelAnimationFrame(matchEndAnimRafRef.current);
        matchEndAnimRafRef.current = null;
      }
      setMatchEndDisplayScores(null);
      return;
    }
    const finalScores = [
      Number(belotEnginePublic.teamScores[0]) || 0,
      Number(belotEnginePublic.teamScores[1]) || 0,
    ];
    const fromScores = matchEndDisplayScores || [
      Math.max(0, finalScores[0] - 1620),
      Math.max(0, finalScores[1] - 1620),
    ];
    const startedAt = performance.now();
    const duration = 1100;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const tick = (now) => {
      const t = Math.min(1, (now - startedAt) / duration);
      const f = ease(t);
      setMatchEndDisplayScores([
        Math.round(fromScores[0] + (finalScores[0] - fromScores[0]) * f),
        Math.round(fromScores[1] + (finalScores[1] - fromScores[1]) * f),
      ]);
      if (t < 1) {
        matchEndAnimRafRef.current = requestAnimationFrame(tick);
      } else {
        matchEndAnimRafRef.current = null;
      }
    };
    if (matchEndAnimRafRef.current) cancelAnimationFrame(matchEndAnimRafRef.current);
    matchEndAnimRafRef.current = requestAnimationFrame(tick);
    return () => {
      if (matchEndAnimRafRef.current) cancelAnimationFrame(matchEndAnimRafRef.current);
      matchEndAnimRafRef.current = null;
    };
  }, [belotPhase, belotEnginePublic?.teamScores]); // eslint-disable-line react-hooks/exhaustive-deps -- matchEndDisplayScores в deps перезапускает RAF

  const belotDisplayName = useMemo(() => {
    if (belotVariant === 'moldovan') return 'Молдавский белот';
    if (isOpenDuellBelot) return 'Открытый белот 1×1';
    return 'Армянский белот';
  }, [belotVariant, isOpenDuellBelot]);

  const belotMatchEndHeroOutcome = useMemo(() => {
    if (belotPhase !== 'match_end' || !belotEnginePublic || !Array.isArray(belotEnginePublic.teamScores)) {
      return { heroWonMatch: false, heroLostMatch: false, matchEndNeutral: true };
    }
    const tgt = belotScoreSheetTarget;
    const s0 = Number(belotEnginePublic.teamScores[0]) || 0;
    const s1 = Number(belotEnginePublic.teamScores[1]) || 0;
    if (myBelotTeam !== 0 && myBelotTeam !== 1) {
      return { heroWonMatch: false, heroLostMatch: false, matchEndNeutral: true };
    }
    const us = myBelotTeam === 0 ? s0 : s1;
    const them = myBelotTeam === 0 ? s1 : s0;
    if (us >= tgt && us > them) return { heroWonMatch: true, heroLostMatch: false, matchEndNeutral: false };
    if (them >= tgt && them > us) return { heroWonMatch: false, heroLostMatch: true, matchEndNeutral: false };
    if (us >= tgt && them >= tgt && us === them) {
      return { heroWonMatch: false, heroLostMatch: false, matchEndNeutral: true };
    }
    if (us >= tgt) return { heroWonMatch: true, heroLostMatch: false, matchEndNeutral: false };
    if (them >= tgt) return { heroWonMatch: false, heroLostMatch: true, matchEndNeutral: false };
    return { heroWonMatch: false, heroLostMatch: false, matchEndNeutral: true };
  }, [belotPhase, belotEnginePublic, belotScoreSheetTarget, myBelotTeam]);

  const belotMatchEndHeadline = useMemo(() => {
    if (belotPhase !== 'match_end' || !belotEnginePublic || !Array.isArray(belotEnginePublic.teamScores)) {
      return 'Матч завершён';
    }
    const s0 = Number(belotEnginePublic.teamScores[0]) || 0;
    const s1 = Number(belotEnginePublic.teamScores[1]) || 0;
    const { heroWonMatch, heroLostMatch, matchEndNeutral } = belotMatchEndHeroOutcome;
    if (matchEndNeutral) return 'Матч завершён';
    if (heroWonMatch) {
      const blowout = (s0 > 0 && s1 === 0) || (s1 > 0 && s0 === 0);
      if (blowout) return 'Разгром! 🔥';
      return 'Победа';
    }
    if (heroLostMatch) return 'Поражение';
    return 'Матч завершён';
  }, [belotPhase, belotEnginePublic, belotMatchEndHeroOutcome]);

  const belotMatchEndLines = useMemo(() => {
    if (belotPhase !== 'match_end' || !belotEnginePublic || !Array.isArray(belotEnginePublic.teamScores)) return [];
    const tgt = belotScoreSheetTarget;
    const s0 = Number(belotEnginePublic.teamScores[0]) || 0;
    const s1 = Number(belotEnginePublic.teamScores[1]) || 0;
    const lines = [];
    if (myBelotTeam === 0 || myBelotTeam === 1) {
      const us = myBelotTeam === 0 ? s0 : s1;
      const them = myBelotTeam === 0 ? s1 : s0;
      if (us >= tgt && us > them) lines.push('Победа: ваша команда («мы»).');
      else if (them >= tgt && them > us) lines.push('Победа: соперники («они»).');
      else if (us >= tgt && them >= tgt && us === them) {
        lines.push('Обе команды набрали цель — ничья по счёту.');
      } else if (us >= tgt) lines.push('Победа: ваша команда («мы»).');
      else if (them >= tgt) lines.push('Победа: соперники («они»).');
    }
    if (!lines.length) lines.push('Матч завершён.');
    return lines;
  }, [belotPhase, belotEnginePublic, belotScoreSheetTarget, myBelotTeam]);

  const fmtBelotSheetDelta = (n) => {
    if (n === 0) return '0';
    if (n > 0) return `+${n}`;
    return String(n);
  };

  const {
    seatArrangeMode: belotSeatArrangeMode,
    setSeatArrangeMode: setBelotSeatArrangeMode,
    seatOffsetBySeat: belotSeatOffsetBySeat,
    handleSeatDragPointerDown: handleBelotSeatDragPointerDown,
    handleSeatDragPointerMove: handleBelotSeatDragPointerMove,
    handleSeatDragPointerUp: handleBelotSeatDragPointerUp,
    handleSeatDragLostCapture: handleBelotSeatDragLostCapture,
    handleClearSeatOffsets: handleBelotClearSeatOffsets,
  } = useBelotSeatLayout({
    sessionStarted: colyseusRoomName === 'belot',
    seatArenaRef: belotOpenSeatArenaRef,
    storageScope: 'belot-online',
    storageBlock: String(belotLobbyMaxPlayers || 2),
  });

  return (
    <div className="pt-root relative flex min-h-screen flex-col overflow-hidden bg-background text-foreground">
      {colyseusRoomName === 'belot' ? <ArcadeModeBackdrop mode="belot" /> : null}

      {colyseusRoomName === 'belot' ? (
        <GameIndustrialHeader
          floating
          tableName={`${belotDisplayName} · онлайн`}
          blindsLabel={BELOT_PHASE_RU[belotPhase] || 'онлайн'}
          playersCount={players.length}
          maxPlayers={belotLobbyMaxPlayers}
          bucksBalance={user?.bucksBalance}
          balance={Number.isFinite(Number(user?.balance)) ? Math.max(0, Math.floor(Number(user.balance))) : undefined}
          onLeave={onBack}
          onSeatArrangeToggle={isOpenDuellBelot ? () => setBelotSeatArrangeMode((v) => !v) : undefined}
          seatArrangeMode={isOpenDuellBelot && belotSeatArrangeMode}
          onSeatLayoutReset={isOpenDuellBelot ? handleBelotClearSeatOffsets : undefined}
        />
      ) : (
        <header className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3">
          <div>
            <h1 className="text-lg font-bold">{displayTitle}</h1>
            <p className="text-xs text-muted-foreground font-mono">
              tableId: {tableId}
              {gameRoomId ? ` · room: ${gameRoomId}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onBack}
            className="rounded-lg border border-border px-3 py-1.5 text-sm font-medium hover:bg-muted/50"
          >
            Назад
          </button>
        </header>
      )}

      {loading && (
        <div className="relative z-10 flex flex-1 items-center justify-center p-8">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-neon-cyan border-t-transparent" />
        </div>
      )}

      {error && !loading && (
        <div className="relative z-10 mx-4 mt-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* У belot `GameIndustrialHeader floating` даёт spacer под fixed-шапку — не убирать pt без проверки верстки. */}
      <main
        className={
          colyseusRoomName === 'belot'
            ? 'relative z-10 mx-auto flex min-h-0 w-full max-w-[1180px] flex-1 flex-col gap-3 overflow-y-auto overscroll-y-contain px-2 pb-3 pt-2 [scroll-padding-top:calc(6.5rem+env(safe-area-inset-top,0px))] sm:px-3 sm:pb-4'
            : 'relative z-10 mx-auto flex w-full max-w-[1180px] flex-1 flex-col gap-3 px-2 pb-3 pt-2 [scroll-padding-top:calc(6.5rem+env(safe-area-inset-top,0px))] sm:px-3 sm:pb-4'
        }
      >
        {inviteVisible && incomingInvite && (
          <div className="rounded-xl border border-amber-400/45 bg-amber-500/15 px-3 py-2 shadow-[0_10px_24px_rgba(245,158,11,0.25)]">
            <button
              type="button"
              className="w-full text-left"
              onClick={() => setShowInvitePicker((v) => !v)}
            >
              <div className="text-[11px] font-black uppercase tracking-wide text-amber-200">Вызов на стол</div>
              <div className="mt-1 text-sm font-semibold text-amber-50">
                {incomingInvite.fromUsername || 'Друг'} вызывает на {Number(incomingInvite.stake || 0)}.
              </div>
            </button>
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                className="rounded-lg border border-emerald-400/40 bg-emerald-500/20 px-3 py-1.5 text-xs font-bold text-emerald-100"
                onClick={() => setShowInvitePicker(true)}
              >
                Принять
              </button>
              <button
                type="button"
                className="rounded-lg border border-rose-400/40 bg-rose-500/20 px-3 py-1.5 text-xs font-bold text-rose-100"
                onClick={() => resolveInvite('declined')}
              >
                Отклонить
              </button>
            </div>
            {showInvitePicker && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-cyan-400/40 bg-cyan-500/20 px-3 py-1.5 text-xs font-bold text-cyan-100"
                  onClick={() => resolveInvite('accept-now')}
                >
                  Выйти сейчас
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-violet-400/40 bg-violet-500/20 px-3 py-1.5 text-xs font-bold text-violet-100"
                  onClick={() => resolveInvite('accept-after-hand')}
                >
                  После раздачи
                </button>
              </div>
            )}
          </div>
        )}

        {colyseusRoomName === 'belot' && isBelotVariantSupported && belotEnginePublic && (
          <section
            className="relative flex min-h-0 flex-1 flex-col rounded-xl border border-border/45 bg-card/15 p-2 sm:p-3"
            data-belot-hydrate-suppress={belotHydrateSuppressAnim ? '1' : '0'}
          >
            {!isOpenDuellBelot ? (
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-semibold text-violet-200">{belotDisplayName}</h2>
                  {belotStake > 0 ? (
                    <span className="rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-100/85">
                      Ставка {belotStake}
                    </span>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {myBelotSeat != null ? (
                    <ArmenianBelotScoreSheetTrigger onClick={() => setBelotScoreSheetOpen(true)} />
                  ) : null}
                </div>
              </div>
            ) : null}

            {belotLobbyMaxPlayers === 2 && !isOpenDuellBelot ? (
              <p className="mb-2 rounded-lg border border-violet-400/20 bg-violet-950/25 px-2.5 py-1.5 text-[10px] leading-snug text-violet-100/85">
                Дуэль (2 игрока в лобби): за столом всё равно четыре места (команды 0+2 и 1+3). Свободные места
                без человека ходят автоматически — можно начинать раздачу вдвоём.
              </p>
            ) : (
              <p className="mb-2 rounded-lg border border-violet-400/15 bg-violet-950/20 px-2.5 py-1.5 text-[10px] leading-snug text-violet-100/75">
                Четыре места за столом: пустые места в торгах и взятках заполняет сервер, пока не подключатся люди.
              </p>
            )}

            {(belotPhase === 'init' || belotPhase === 'dealing') ? (
              <div className="mb-3 rounded-xl border border-violet-400/25 bg-gradient-to-b from-violet-950/30 to-violet-950/10 p-3 shadow-inner">
                <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] uppercase tracking-[0.14em] text-violet-300/70">
                  <span>Превью партии</span>
                  <span className="font-mono text-violet-100/85">
                    {players.length}/{belotLobbyMaxPlayers} в лобби
                    {isOpenDuellBelot ? ' · дуэль 1×1' : ' · 4 места за столом'}
                  </span>
                </div>
                <p className="mt-1 text-base font-black text-violet-50">{belotDisplayName}</p>
                <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] sm:grid-cols-4">
                  <div className="rounded-lg border border-white/10 bg-black/30 px-2 py-1.5">
                    <p className="text-[9px] uppercase tracking-wider text-violet-300/65">Цель</p>
                    <p className="font-mono text-sm font-semibold text-violet-50">
                      {belotMatchTargetCpFromEngine(belotScoreSheetTarget)} CP
                    </p>
                  </div>
                  <div className="rounded-lg border border-white/10 bg-black/30 px-2 py-1.5">
                    <p className="text-[9px] uppercase tracking-wider text-violet-300/65">Ставка</p>
                    <p className="font-mono text-sm font-semibold text-amber-100/95">
                      {belotStake > 0 ? belotStake : '—'}
                    </p>
                  </div>
                  <div className="rounded-lg border border-white/10 bg-black/30 px-2 py-1.5">
                    <p className="text-[9px] uppercase tracking-wider text-violet-300/65">Команды</p>
                    <p className="font-mono text-xs font-semibold text-violet-50/90">
                      {isOpenDuellBelot ? '1×1' : '0+2 / 1+3'}
                    </p>
                  </div>
                  <div className="rounded-lg border border-white/10 bg-black/30 px-2 py-1.5">
                    <p className="text-[9px] uppercase tracking-wider text-violet-300/65">Раунд</p>
                    <p className="font-mono text-sm font-semibold text-violet-50">
                      {(belotEnginePublic?.roundIndex ?? 0) + 1}
                    </p>
                  </div>
                </div>
                {myBelotSeat != null ? (
                  <p className="mt-2 text-[11px] text-violet-100/85">
                    Вы на месте <span className="font-bold text-violet-50">{myBelotSeat}</span>
                    {isOpenDuellBelot ? (
                      <> · дуэль 1×1</>
                    ) : (
                      <>
                        {' · команда '}
                        <span className="font-bold text-violet-50">
                          {belotTeamOfSeat(myBelotSeat) === 0 ? 'МЫ (0+2)' : 'ОНИ (1+3)'}
                        </span>
                      </>
                    )}
                  </p>
                ) : (
                  <p className="mt-2 text-[11px] text-violet-100/65">
                    Вы пока не за столом. Дождитесь, пока сервер посадит вас на свободное место.
                  </p>
                )}
              </div>
            ) : null}

            {isOpenDuellBelot ? (
              <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              {openDuellLegalPlayEmptyWhileMyTurn ? (
                <div
                  role="alert"
                  className="mx-1 mb-2 shrink-0 rounded-lg border border-amber-500/70 bg-amber-950/55 px-3 py-2 text-[11px] leading-snug text-amber-50 shadow-md sm:mx-0 sm:text-xs"
                >
                  <span className="font-semibold">Нет доступных ходов (ошибка клиента).</span>{' '}
                  Сейчас ваш ход в розыгрыше, но список разрешённых карт пуст — часто это рассинхрон приватной руки/стола
                  с публичным состоянием. Откройте отладку движка и экспортируйте JSON (кнопка внизу стола).
                </div>
              ) : null}
              <OnlineOpenBelotTable
                belotOpenDuell={belotOpenDuell}
                belotPhase={belotPhase}
                mySeat={myBelotSeat}
                myHand={myBelotHand}
                myTableStacks={myBelotTableStacks}
                belotSeatRows={belotSeatRows}
                onPlayCard={({ card, from, columnIndex }) => {
                  sendBelotCommand({
                    type: 'PLAY_CARD',
                    payload: { card, from, columnIndex },
                  });
                }}
                canPlay={Boolean(myBelotTurnPlay) && !belotOnlineInteractionBlocked}
                legalPlayCardIds={openDuellLegalPlayCardIds}
                bellaNotice={openDuellBellaNotice}
                heroSecLeft={heroSecLeft}
                heroTurnSeconds={ONLINE_BELOT_HERO_TURN_SECONDS}
                oppTurnDeadlineAtMs={snapshot?.belotTurnDeadlineAtMs ?? null}
                play={play}
                audioSuppressed={belotAudioSuppressed}
                suppressDealAnimation={belotHydrateSuppressAnim}
                onRoundDealAnimActiveChange={setBelotDealAnimActive}
                onPlayFlightActiveChange={setBelotPlayFlightActive}
                belotPlayResyncKey={Number(belotSync?.seq) || 0}
                onOpenScoreSheet={myBelotSeat != null ? () => setBelotScoreSheetOpen(true) : undefined}
                onPeekLastTrick={() => setBelotPeekLastTrick(true)}
                onOpenEngineDebug={() => setOpenDuellEngineDebugOpen(true)}
                contractBadgeAnchorRef={contractBadgeAnchorRef}
                seatArenaRef={belotOpenSeatArenaRef}
                seatArrangeMode={belotSeatArrangeMode}
                seatOffsetBySeat={belotSeatOffsetBySeat}
                handleSeatDragPointerDown={handleBelotSeatDragPointerDown}
                handleSeatDragPointerMove={handleBelotSeatDragPointerMove}
                handleSeatDragPointerUp={handleBelotSeatDragPointerUp}
                handleSeatDragLostCapture={handleBelotSeatDragLostCapture}
                statusBanner={
                  belotRevealFx ? null : belotPhase === 'auction' &&
                    belotTurnSeat !== null &&
                    !myBelotTurnAuction ? (
                    <>
                      Торги: ход у {belotSeatRows[belotTurnSeat]?.name || `место ${belotTurnSeat}`}
                      {belotAuction?.highestBid ? (
                        <> · сейчас {formatAuctionBid(belotAuction.highestBid)}</>
                      ) : null}
                    </>
                  ) : isOpenDuellBelot &&
                    belotPhase === 'play' &&
                    myBelotTurnPlay &&
                    (belotTurnSeat === 2 || belotTurnSeat === 3) ? (
                    <>Ваш ход — с верха вашего стола (колонка).</>
                  ) : belotPhase === 'play' && !myBelotTurnPlay && belotTurnSeat !== null ? (
                    <>
                      Сейчас ход{' '}
                      {belotSeatRows[belotTurnSeat === 0 || belotTurnSeat === 2 ? 0 : 1]?.name ||
                        `место ${belotTurnSeat}`}
                    </>
                  ) : null
                }
                heroAuctionPanel={
                  myBelotTurnAuction && !belotOnlineInteractionBlocked ? (
                    <ArmenianAuctionPanel
                      key={`belot-auction-open-${belotAuctionRemountKey}-${belotEnginePublic?.roundIndex ?? 0}-${belotTurnSeat ?? 'x'}`}
                      open
                      presentation="inline"
                      variant={belotAuctionPanelVariant}
                      passLabel={belotAuctionPassLabel}
                      orderLabel={belotAuctionOrderLabel}
                      turnHeadline={belotAuctionTurnHeadline}
                      highestBid={belotAuction?.highestBid ?? null}
                      onPass={() => {
                        play('fold', 0.32);
                        sendBelotCommand({ type: 'PASS' });
                      }}
                      onBid={(bid) => {
                        play('belot-auction-click', 0.52);
                        sendBelotCommand({ type: 'BID', bid });
                      }}
                      contraMultiplier={contraMult}
                      canContra={false}
                      canRecontra={false}
                      auctionMultIsMax={auctionMultIsMax}
                      onContra={() => {}}
                      onRecontra={() => {}}
                      playUi={play}
                    />
                  ) : null
                }
              />
              </div>
            ) : (
              <>
              <OnlineBelotTable
                belotArmenian={belotArmenian}
                belotSeatRows={belotSeatRows}
                mySeat={myBelotSeat}
                myHand={myBelotHand}
                onPlayCard={(card) => {
                  /* Звук хода — только useBelotOnlineFxBus по снапшоту (иначе двойной удар). */
                  sendBelotCommand({ type: 'PLAY_CARD', payload: { card } });
                }}
                shulerEnabled={Boolean(belotArmenian?.shulerEnabled)}
                shulerLegalCardIds={armenianShulerLegalCardIds}
                onAccuseDirty={(card) => {
                  sendBelotCommand({ type: 'ACCUSE_DIRTY', payload: { card } });
                }}
                canPlay={Boolean(myBelotTurnPlay) && !belotOnlineInteractionBlocked}
                contractBadgeAnchorRef={contractBadgeAnchorRef}
                deckRef={deckRef}
                trickCenterRef={trickCenterRef}
                heroSeatAnchorRef={heroSeatAnchorRef}
                heroSecLeft={heroSecLeft}
                heroTurnSeconds={ONLINE_BELOT_HERO_TURN_SECONDS}
                onPeekLastTrick={() => setBelotPeekLastTrick(true)}
                onOpenScoreSheet={() => setBelotScoreSheetOpen(true)}
                heroName={user?.username || 'Вы'}
                targetScore={belotScoreSheetTarget}
                play={play}
                audioSuppressed={belotAudioSuppressed}
                suppressDealAnimation={belotHydrateSuppressAnim}
                onRoundDealAnimActiveChange={setBelotDealAnimActive}
                onPlayFlightActiveChange={setBelotPlayFlightActive}
                belotPlayResyncKey={Number(belotSync?.seq) || 0}
                belotSyncSeq={Number(belotSync?.seq) || 0}
                runQueuedComboCardReveal={runQueuedOnlineComboCardReveal}
                onRegisterHardcoreSelfPreviewEnqueue={registerHardcoreSelfPreviewEnqueue}
                statusBanner={
                  belotRevealFx ? null : belotPhase === 'auction' && belotTurnSeat !== null && !myBelotTurnAuction ? (
                    <>
                      Торги: ход у {belotSeatRows[belotTurnSeat]?.name || `место ${belotTurnSeat}`}
                      {belotAuction?.highestBid ? (
                        <> · сейчас {formatAuctionBid(belotAuction.highestBid)}</>
                      ) : null}
                    </>
                  ) : belotPhase === 'declare' ? (
                    <>Сравнение объявлений перед началом розыгрыша.</>
                  ) : belotPhase === 'play' && !myBelotTurnPlay && belotTurnSeat !== null ? (
                    <>Сейчас ход {belotSeatRows[belotTurnSeat]?.name || `место ${belotTurnSeat}`}</>
                  ) : null
                }
                heroAuctionPanel={
                  myBelotTurnAuction && !belotOnlineInteractionBlocked ? (
                    <ArmenianAuctionPanel
                      key={`belot-auction-${belotAuctionRemountKey}-${belotArmenian?.roundIndex ?? 0}-${belotTurnSeat ?? 'x'}`}
                      open
                      presentation="inline"
                      variant={belotAuctionPanelVariant}
                      passLabel={belotAuctionPassLabel}
                      orderLabel={belotAuctionOrderLabel}
                      turnHeadline={belotAuctionTurnHeadline}
                      highestBid={belotAuction?.highestBid ?? null}
                      onPass={() => {
                        play('fold', 0.32);
                        sendBelotCommand({ type: 'PASS' });
                      }}
                      onBid={(bid) => {
                        play('belot-auction-click', 0.52);
                        sendBelotCommand({ type: 'BID', bid });
                      }}
                      contraMultiplier={contraMult}
                      canContra={canBelotContra}
                      canRecontra={canBelotRecontra}
                      auctionMultIsMax={auctionMultIsMax}
                      onContra={() => {
                        /* belot-contra-hit — в шине по дельте contraMultiplier */
                        sendBelotCommand({ type: 'CONTRA' });
                      }}
                      onRecontra={() => {
                        play('notification', 0.62);
                        sendBelotCommand({ type: 'RECONTRA' });
                      }}
                      playUi={play}
                    />
                  ) : null
                }
                bottomActions={
                  <>
                    <button
                      type="button"
                      disabled={!canBelotStartRound || belotOnlineInteractionBlocked}
                      onClick={() => {
                        /* shuffle-deal — в шине по смене roundIndex */
                        sendBelotCommand({ type: 'START_ROUND' });
                      }}
                      className="flex min-h-[2.75rem] flex-col items-center justify-center rounded-lg border border-violet-400/50 bg-violet-500/20 px-3 py-1.5 text-center text-xs font-semibold text-violet-100 disabled:opacity-40"
                    >
                      <span>Начать раздачу</span>
                      <span className="mt-0.5 max-w-[14rem] text-[9px] font-normal leading-tight text-violet-200/80">
                        Пустые места без игрока ходят автоматически (не нужно ждать четверых).
                      </span>
                    </button>
                    {belotPhase === 'declare' && myBelotSeat !== null && !belotOnlineInteractionBlocked ? (
                      <div className="flex w-full flex-col gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            play('belot-auction-click', 0.4);
                            sendBelotCommand({ type: 'END_DECLARE_PHASE' });
                          }}
                          className="rounded-lg border border-amber-400/40 bg-amber-500/15 px-3 py-2 text-xs font-medium"
                        >
                          Закончить объявления
                        </button>
                        <p className="text-[10px] text-muted-foreground">
                          Казуальный режим: по нажатию сервер сам сравнивает все комбинации на раздаче.
                        </p>
                      </div>
                    ) : null}
                  </>
                }
              />
              {onlineComboRevealPortal}
              {onlineHardcoreCombos.length > 0 && !belotOnlineInteractionBlocked ? (
                <BelotHardcoreDeclareCombosPanel
                  open
                  combos={onlineHardcoreCombos}
                  onCommitKeys={(keys) => confirmOnlineHardcoreDeclarations(keys)}
                  playUi={belotAudioSuppressed ? undefined : play}
                  audioSuppressed={belotAudioSuppressed}
                />
              ) : null}
              </>
            )}

            {belotEconomy ? (
              <div className="mt-3 rounded-lg border border-amber-500/20 bg-amber-500/[0.06] px-3 py-2 text-[10px] text-amber-100/80">
                Банк {Number(belotEconomy.potGross) || 0} · Burn {Number(belotEconomy.rakeAmount) || 0} · Net {Number(belotEconomy.potNet) || 0}
              </div>
            ) : null}
          </section>
        )}

      </main>

      {colyseusRoomName === 'belot' &&
      belotPeekLastTrick &&
      belotEnginePublic &&
      Array.isArray(belotEnginePublic.lastCompletedTrick?.plays) &&
      belotEnginePublic.lastCompletedTrick.plays.length === 4 ? (
        <div
          className="fixed inset-0 z-[58] flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]"
          onClick={() => setBelotPeekLastTrick(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-md rounded-2xl border border-neon-gold/35 bg-[#0a1220]/96 p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Последняя завершённая взятка"
          >
            <p className="text-center text-[11px] font-bold uppercase tracking-wide text-neon-gold">
              Последняя взятка · №{belotEnginePublic.lastCompletedTrick.trickIndex}
            </p>
            <p className="mt-1 text-center text-xs text-slate-300">
              Забрал:{' '}
              {typeof belotEnginePublic.lastCompletedTrick.winnerSeat === 'number'
                ? belotSeatRows[
                    isOpenDuellBelot
                      ? belotEnginePublic.lastCompletedTrick.winnerSeat === 0 ||
                        belotEnginePublic.lastCompletedTrick.winnerSeat === 2
                        ? 0
                        : 1
                      : belotEnginePublic.lastCompletedTrick.winnerSeat
                  ]?.name || `место ${belotEnginePublic.lastCompletedTrick.winnerSeat}`
                : '—'}
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              {belotEnginePublic.lastCompletedTrick.plays.map((p, i) => {
                const seat = typeof p?.seat === 'number' ? p.seat : i;
                const card = p?.card;
                const { rank, suit } = belotCardIdToPlaying(card);
                const rowSeat =
                  isOpenDuellBelot ? (seat === 0 || seat === 2 ? 0 : 1) : seat;
                const isHeroPlay = myBelotSeat != null && seat === myBelotSeat;
                return (
                  <div key={`${seat}-${i}`} className="flex flex-col items-center gap-1">
                    <div
                      className={cn(
                        'rounded-xl',
                        isHeroPlay && 'p-[3px] ring-2 ring-cyan-300/95 ring-offset-0',
                      )}
                    >
                      <PlayingCard
                        rank={rank}
                        suit={suit}
                        size="sm"
                        className={cn(
                          'shadow-xl',
                          Boolean(p?.dirty) &&
                            !p?.caughtResolved &&
                            'ring-2 ring-amber-400/80',
                        )}
                      />
                    </div>
                    <span className="max-w-[5rem] truncate text-[10px] text-muted-foreground">
                      {belotSeatRows[rowSeat]?.name || `м${seat}`}
                    </span>
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => setBelotPeekLastTrick(false)}
              className="mt-5 w-full rounded-xl border border-white/15 py-2 text-sm font-semibold text-slate-200 hover:bg-white/10"
            >
              Закрыть
            </button>
          </div>
        </div>
      ) : null}

      {colyseusRoomName === 'belot' &&
      isBelotVariantSupported &&
      belotEnginePublic &&
      belotPhase === 'match_end' ? (
        <div
          className="fixed inset-0 z-[59] flex items-center justify-center overflow-y-auto bg-black/50 p-4"
          aria-live="assertive"
        >
          <div
            className={cn(
              'max-h-[min(92vh,640px)] w-full max-w-md overflow-y-auto rounded-2xl border bg-gradient-to-b px-5 py-6 text-center',
              belotMatchEndHeroOutcome.heroWonMatch &&
                'poker-fx-win-panel poker-fx-win-aura border-emerald-400/45 from-emerald-950/95 to-[#0a1520]/98 shadow-[0_0_60px_rgba(34,197,94,0.25)]',
              belotMatchEndHeroOutcome.heroLostMatch &&
                'poker-fx-loss-panel border-rose-400/45 from-rose-950/95 to-[#0a1520]/98 shadow-[0_0_60px_rgba(244,63,94,0.25)]',
              belotMatchEndHeroOutcome.matchEndNeutral &&
                'poker-fx-win-panel border-amber-400/45 from-amber-950/95 to-[#0a1520]/98 shadow-[0_0_60px_rgba(251,191,36,0.25)]',
            )}
          >
            {belotMatchEndHeroOutcome.heroWonMatch ? (
              <Trophy className="mx-auto mb-2 h-10 w-10 text-emerald-300" aria-hidden />
            ) : belotMatchEndHeroOutcome.heroLostMatch ? (
              <Skull className="mx-auto mb-2 h-10 w-10 text-rose-300" aria-hidden />
            ) : (
              <Sparkles className="mx-auto mb-2 h-10 w-10 text-amber-300" aria-hidden />
            )}
            <p
              className={cn(
                'text-lg font-black',
                belotMatchEndHeroOutcome.heroWonMatch && 'text-emerald-50',
                belotMatchEndHeroOutcome.heroLostMatch && 'text-rose-50',
                belotMatchEndHeroOutcome.matchEndNeutral && 'text-white',
              )}
            >
              {belotMatchEndHeadline}
            </p>
            {isOpenDuellBelot ? (
              <p className="mt-0.5 text-[11px] font-medium text-amber-100/75">{belotDisplayName}</p>
            ) : null}
            <p className="mt-2 font-mono text-2xl font-bold tabular-nums text-amber-100">
              {Array.isArray(matchEndDisplayScores)
                ? `${belotMatchCpFromEnginePoints(matchEndDisplayScores[0])} : ${belotMatchCpFromEnginePoints(matchEndDisplayScores[1])}`
                : Array.isArray(belotEnginePublic.teamScores)
                  ? `${belotMatchCpFromEnginePoints(belotEnginePublic.teamScores[0])} : ${belotMatchCpFromEnginePoints(belotEnginePublic.teamScores[1])}`
                  : '—'}
              <span className="ml-1 text-sm font-semibold text-amber-200/80">CP</span>
            </p>
            <p
              className={cn(
                'mt-1 text-xs',
                belotMatchEndHeroOutcome.heroWonMatch && 'text-emerald-100/85',
                belotMatchEndHeroOutcome.heroLostMatch && 'text-rose-100/85',
                belotMatchEndHeroOutcome.matchEndNeutral && 'text-amber-100/80',
              )}
            >
              Цель: {belotMatchTargetCpFromEngine(belotScoreSheetTarget)} CP
            </p>
            {belotEconomy ? (
              <>
                <p
                  className={cn(
                    'mt-1 text-xs',
                    belotMatchEndHeroOutcome.heroWonMatch && 'text-emerald-100/80',
                    belotMatchEndHeroOutcome.heroLostMatch && 'text-rose-100/80',
                    belotMatchEndHeroOutcome.matchEndNeutral && 'text-amber-100/80',
                  )}
                >
                  Банк: {Number(belotEconomy.potGross) || 0} · Комиссия {Number(belotEconomy.rakePercent) || 2}%:{' '}
                  {Number(belotEconomy.rakeAmount) || 0} · Выплата команде: {Number(belotEconomy.potNet) || 0}
                </p>
                <p
                  className={cn(
                    'text-xs',
                    belotMatchEndHeroOutcome.heroWonMatch && 'text-emerald-100/80',
                    belotMatchEndHeroOutcome.heroLostMatch && 'text-rose-100/80',
                    belotMatchEndHeroOutcome.matchEndNeutral && 'text-amber-100/80',
                  )}
                >
                  Выплата на победителя: {Number(belotEconomy.winnerShare) || 0}
                </p>
              </>
            ) : null}
            <ul
              className={cn(
                'mt-3 space-y-1 text-left text-xs leading-snug',
                belotMatchEndHeroOutcome.heroWonMatch && 'text-emerald-50/90',
                belotMatchEndHeroOutcome.heroLostMatch && 'text-rose-50/90',
                belotMatchEndHeroOutcome.matchEndNeutral && 'text-amber-50/90',
              )}
            >
              {belotMatchEndLines.map((line, i) => (
                <li key={i}>• {line}</li>
              ))}
            </ul>
            <p
              className={cn(
                'mt-2 text-[10px] leading-snug',
                belotMatchEndHeroOutcome.heroWonMatch && 'text-emerald-100/75',
                belotMatchEndHeroOutcome.heroLostMatch && 'text-rose-100/75',
                belotMatchEndHeroOutcome.matchEndNeutral && 'text-amber-100/70',
              )}
            >
              Реванш: когда все игроки за столом нажмут «Готов к реваншу», сервер начнёт новую раздачу.
            </p>
            <p
              className={cn(
                'mt-1 text-center text-[11px] font-semibold tabular-nums',
                belotMatchEndHeroOutcome.heroWonMatch && 'text-emerald-200/90',
                belotMatchEndHeroOutcome.heroLostMatch && 'text-rose-200/90',
                belotMatchEndHeroOutcome.matchEndNeutral && 'text-amber-200/90',
              )}
            >
              Готово к реваншу: {belotRematchReadyCount} / {belotRematchVoteTotal}
              {belotRematchSecLeft != null && belotRematchDeadlineAtMs != null ? (
                <span className="mt-0.5 block text-[10px] font-medium opacity-90">
                  {belotRematchSecLeft <= 0
                    ? 'Время вышло — непроголосовавших снимет сервер.'
                    : `До снятия с места: ${belotRematchSecLeft} с (лимит ${Math.round(belotRematchBudgetMs / 1000)} с).`}
                </span>
              ) : null}
            </p>
            {belotSeatRows.length > 0 ? (
              <ul className="mt-2 space-y-0.5 text-left text-[10px] leading-snug text-amber-100/75">
                {belotSeatRows
                  .filter((row) => String(row.uid || '').trim())
                  .map((row) => {
                    const voted = belotRematchReadyIdSet.has(String(row.uid));
                    return (
                      <li key={row.uid}>
                        • {row.name}: {voted ? 'готов' : 'ждём'}
                      </li>
                    );
                  })}
              </ul>
            ) : null}
            {belotScoreSheetRows.length > 0 ? (
              <div className="mt-4 rounded-lg border border-amber-900/35 bg-[#faf6ea] p-2 text-left text-amber-950 shadow-inner">
                <p className="mb-1 text-center font-serif text-[11px] font-bold text-amber-950">Листок партии</p>
                <table className="w-full border-collapse font-mono text-[10px] tabular-nums sm:text-xs">
                  <thead>
                    <tr className="border-b border-amber-900/25 bg-[#e8dcc4] font-serif">
                      <th className="px-1 py-1">#</th>
                      <th className="px-1 py-1">Мы</th>
                      <th className="px-1 py-1">Они</th>
                      <th className="hidden px-1 py-1 sm:table-cell">Σ</th>
                      <th className="hidden px-1 py-1 sm:table-cell">Σ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {belotScoreSheetRows.map((r) => (
                      <tr key={r.roundOrdinal} className="border-b border-amber-900/10 last:border-0">
                        <td className="px-1 py-1">{r.roundOrdinal}</td>
                        <td className="px-1 py-1">{fmtBelotSheetDelta(r.deltaUs)}</td>
                        <td className="px-1 py-1">{fmtBelotSheetDelta(r.deltaThem)}</td>
                        <td className="hidden px-1 py-1 font-semibold sm:table-cell">{r.cumUs}</td>
                        <td className="hidden px-1 py-1 font-semibold sm:table-cell">{r.cumThem}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            <div className="mt-5 flex flex-col gap-2">
              <button
                type="button"
                disabled={
                  !user?.id ||
                  myBelotSeat === null ||
                  belotOnlineInteractionBlocked ||
                  heroBelotRematchReady ||
                  belotRematchVoteTotal < 1
                }
                onClick={() => {
                  play('button-click', 0.35);
                  sendBelotCommand({ type: 'REMATCH_READY' });
                }}
                className={cn(
                  'rounded-xl border py-3 text-sm font-bold disabled:opacity-40',
                  belotMatchEndHeroOutcome.heroWonMatch &&
                    'border-emerald-400/55 bg-emerald-950/55 text-emerald-100',
                  belotMatchEndHeroOutcome.heroLostMatch &&
                    'border-rose-400/55 bg-rose-950/45 text-rose-100',
                  belotMatchEndHeroOutcome.matchEndNeutral &&
                    'border-amber-400/55 bg-amber-950/55 text-amber-100',
                )}
              >
                {heroBelotRematchReady ? 'Вы готовы к реваншу' : 'Готов к реваншу'}
              </button>
              <button
                type="button"
                onClick={onBack}
                className="rounded-xl border border-red-500/45 bg-red-950/50 py-3 text-sm font-bold text-red-200"
              >
                Выйти
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {colyseusRoomName === 'belot' ? (
        <ArmenianBelotScoreSheetPanel
          open={belotScoreSheetOpen}
          onClose={() => setBelotScoreSheetOpen(false)}
          targetScore={belotScoreSheetTarget}
          rows={belotScoreSheetRows}
        />
      ) : null}

      {toasts.map((t) => (
        <NotificationToast
          key={t.id}
          message={t.message}
          type={t.type}
          onClose={() => removeToast(t.id)}
        />
      ))}

      {sessionOutcomeOverlay || matchAbortOverlayOutcome ? (
        <SessionResultOverlay
          outcome={matchAbortOverlayOutcome || sessionOutcomeOverlay}
          onClose={() => {
            if (matchAbortOverlayActiveRef.current) {
              matchAbortOverlayActiveRef.current = false;
              setMatchAbortOverlayOutcome(null);
              matchAbortPostOverlayRef.current?.();
              matchAbortPostOverlayRef.current = null;
              matchAbortPendingPayloadRef.current = null;
              return;
            }
            setSessionOutcomeOverlay(null);
          }}
        />
      ) : null}

      <BelotOnlineRevealOverlay fx={belotRevealFx} />

      {isOpenDuellBelot ? (
        <OpenDuellOnlineEngineDebugPanel
          open={openDuellEngineDebugOpen}
          onClose={() => setOpenDuellEngineDebugOpen(false)}
          onClearDebug={() => {
            setOpenDuellClientCmdLog([]);
            setOpenDuellSnapTimeline([]);
          }}
          report={openDuellDebugReport}
        />
      ) : null}
    </div>
  );
}

export default function BaseGameRoomScreen(props) {
  return (
    <AudioProvider>
      <BaseGameRoomScreenInner {...props} />
    </AudioProvider>
  );
}
